import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';
import { redactDeep, redactHeaders } from './redact';
import { isCanceledError, isNetworkNoise, isWebsocketNoise } from './http';

const APP_ENV = import.meta.env.MODE === 'development' ? 'local' : import.meta.env.REACT_APP_ENVR;
const DSN = import.meta.env.REACT_APP_SENTRY;

const ENABLED_ENVS = new Set(['prod', 'dev']);

export const isEnabled = () => Boolean(DSN) && ENABLED_ENVS.has(APP_ENV);
export function sample(p: number) {
    return Math.random() < p;
}

export function setSentryUser(id: number | string | null) {
    if (!isEnabled()) return;
    Sentry.setUser(id == null ? null : { id: String(id) });
}

export function initSentry() {
    const enabled = Boolean(DSN) && ENABLED_ENVS.has(APP_ENV);
    if (!enabled) return;

    Sentry.init({
        dsn: DSN,
        enabled,
        environment: APP_ENV,
        release: __APP_VERSION__,
        sendDefaultPii: false,
        _experiments: { enableLogs: true },
        debug: false,

        integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
                _experiments: { enableInteractions: true },
            }),
            Sentry.browserProfilingIntegration(),
        ],

        tracesSampler: (ctx) => {
            const name = ctx?.transactionContext?.name || ctx?.name || '';
            if (name.includes('/health') || name.includes('/metrics')) return 0.0;
            if (name.includes('poll') || name.includes('heartbeat')) return 0.01;
            return 0.05;
        },
        profileLifecycle: 'trace',
        profileSessionSampleRate: 0.1,

        // Block extensions + common 3rd-party script sources.
        denyUrls: [
            /chrome-extension:\/\//i,
            /moz-extension:\/\//i,
            /safari-extension:\/\//i,
            /extensions\//i,
            /web-sdk\.smartlook\.com/i,
        ],

        ignoreErrors: [
            /extension context invalidated/i,
            /ResizeObserver loop limit exceeded/i,
            /ResizeObserver loop completed with undelivered notifications/i,
        ],

        beforeSend: (event, hint) => {
            // Last line of defense: universal noise + universal redaction
            const ex: any = hint?.originalException;

            // Drop Axios CanceledError (expected when requests are aborted); must run first.
            if (isCanceledError(ex)) return null;

            const url = ex?.__http?.url as string | undefined;

            // Drop network/ws spam
            if (isNetworkNoise(ex) || isWebsocketNoise(ex)) return null;

            // Drop extension-originating frames
            const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
            if (frames.some((f: any) => String(f.filename || '').includes('chrome-extension://'))) {
                return null;
            }

            // Drop errors from browser-injected scripts (e.g. iOS translate) whose frames
            // all report the HTML document URL instead of a bundled file.
            if (isPageOnlyStack(frames)) return null;

            if (event.request?.headers) {
                event.request.headers = redactHeaders(event.request.headers as any);
            }
            if (event.extra) event.extra = redactDeep(event.extra);

            if (event.user) {
                event.user = event.user.id ? { id: String(event.user.id) } : undefined;
            }

            event.tags = {
                ...(event.tags || {}),
                app_env: APP_ENV,
                auth_state: event.user?.id ? 'authenticated' : 'anonymous',
            };
            if (url) event.tags.http_url = normalizeUrl(url);

            return event;
        },

        beforeBreadcrumb: (b) => {
            if (b.category === 'console' && (b.level === 'info' || b.level === 'log')) return null;

            const u = (b.data as any)?.url as string | undefined;
            if (u && /zopim|intercom|googletagmanager|google-analytics/i.test(u)) return null;

            // redact headers if they ever appear in breadcrumbs
            if ((b.data as any)?.headers) {
                (b.data as any).headers = redactHeaders((b.data as any).headers);
            }

            return b;
        },
    });
}

function normalizeUrl(url: string) {
    try {
        const u = new URL(url, window.location.origin);
        return `${u.origin}${u.pathname}`;
    } catch {
        return url;
    }
}

function isPageOnlyStack(frames: { filename?: string }[]) {
    if (!frames.length) return false;
    const page = window.location.origin + window.location.pathname;
    return frames.every((f) => (f.filename || '').split(/[?#]/)[0] === page);
}
